import { UnknownError } from '@cian/peperrors';
import { SmartCaptcha, SmartCaptchaProps } from '@yandex/smart-captcha';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { setCaptchaToken } from '../../actions/chat/actions';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { useApplicationContext } from '../../utils/applicationContext';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';

interface ICaptchaProps {
  chatId?: string;
}

type TJavascriptError = Parameters<NonNullable<SmartCaptchaProps['onJavascriptError']>>[0];

const DOMAIN = `${EExceptionDomain.FirstMessageSend}.captcha`;

export const Captcha: React.FC<ICaptchaProps> = ({ chatId }) => {
  const { logger } = useApplicationContext();

  const dispatch = useDispatch();

  const sitekey = useRuntimeSetting<string>('YandexCaptchaPublicKey', '');

  const onCaptchaSuccess = React.useCallback(
    (token?: string) => {
      if (!chatId) {
        return;
      }

      dispatch(setCaptchaToken({ chatId, captchaToken: token }));
    },
    [dispatch, chatId],
  );

  const handleCaptchaJsError = React.useCallback(
    (jsError: TJavascriptError) => {
      const error = new UnknownError({
        domain: DOMAIN,
        message: 'Ошибка JS при загрузке капчи',
        details: {
          error: JSON.stringify(jsError),
          chatId,
        },
      });

      logger.error(error);
    },
    [chatId, logger],
  );

  const handleCaptchaNetworkError = React.useCallback(() => {
    const error = new UnknownError({
      domain: DOMAIN,
      message: 'Ошибка сети при запросе капчи',
      details: {
        chatId,
      },
    });

    logger.error(error);
  }, [chatId, logger]);

  const handleCaptchaTokenExpired = React.useCallback(() => {
    const exception = new UnknownError({
      domain: DOMAIN,
      message: 'Токен капчи стал невалидным',
      details: {
        chatId,
      },
    });

    logger.warning(exception);

    onCaptchaSuccess();
  }, [logger, chatId, onCaptchaSuccess]);

  if (!sitekey) {
    return null;
  }

  return (
    <SmartCaptcha
      sitekey={sitekey}
      onSuccess={onCaptchaSuccess}
      onJavascriptError={handleCaptchaJsError}
      onNetworkError={handleCaptchaNetworkError}
      onTokenExpired={handleCaptchaTokenExpired}
    />
  );
};
